<template>
  <div class="cb">
    <div class="cb-sub">displaying Cust ID / # of Orders</div>
    <div class="cb-searchwrap">
      <input class="cb-search" type="text" v-model="search" placeholder="Search customer ID or name…" />
      <button v-if="search" type="button" class="cb-clear" @click="search = ''" title="Clear">×</button>
    </div>
    <div class="cb-list" :class="{ 'cb-loading': loading }">
      <div v-for="c in filtered" :key="c.customerId" class="cb-row"
           @click="$emit('select', c.customerId)"
           :title="'Click to add this customer to the filter\n' + c.customerId + (c.customerName ? ' — ' + c.customerName : '') + '\n' + (c.salesRep || 'No rep') + ' · ' + fmtNum(c.orderCount) + ' orders'">
        <img v-if="flagIso(c.country)" class="cb-flag" :src="'https://flagcdn.com/w40/' + flagIso(c.country) + '.png'" :alt="c.country" :title="c.country" @error="$event.target.style.visibility = 'hidden'" />
        <span v-else class="cb-flag cb-flag--none" :title="c.country || ''"></span>
        <div class="cb-id">{{ c.customerId }}</div>
        <div class="cb-bartrack">
          <div class="cb-bar" :style="{ width: barPct(c.orderCount) }">
            <template v-if="segsFor(c).length">
              <div v-for="(s, si) in segsFor(c)" :key="si" class="cb-seg"
                   :style="{ flexGrow: (Number(s.orderCount) || 1), background: segColor(s.territory) }"
                   :title="s.territory + (s.salesRep ? ' · ' + s.salesRep : '') + ' · ' + fmtNum(s.orderCount) + ' orders'"></div>
            </template>
            <div v-else class="cb-seg" :style="{ flexGrow: 1, background: barColor(c) }"></div>
          </div>
          <span class="cb-count">{{ fmtNum(c.orderCount) }}</span>
        </div>
        <!-- Icon cluster (order: 💡 AI perspective · 📝 notation · 📋 specifications). Each slot is always present
             (fixed grid column) so the bars stay aligned whether or not a given customer has a note/spec. -->
        <span class="cb-ic">
          <button v-if="c.customerNotation || c.customerSpecs" type="button" class="cb-persp"
                  @click.stop="$emit('perspective', { customer: c, event: $event })"
                  title="AI sales perspective on this customer's notes &amp; specs">💡</button>
        </span>
        <span class="cb-ic">
          <button v-if="c.customerNotation" type="button" class="cb-note"
                  @click.stop="$emit('notation', { customer: c, event: $event })"
                  title="View customer notation">📝</button>
        </span>
        <span class="cb-ic">
          <button v-if="c.customerSpecs" type="button" class="cb-spec"
                  @click.stop="$emit('specs', { customer: c, event: $event })"
                  title="View customer specifications">📋</button>
        </span>
      </div>
      <div v-if="!filtered.length" class="cb-empty">No customers match.</div>
    </div>
  </div>
</template>

<script>
// Qualitative palette — one stable color per sales rep (matches the Power BI
// "Colored By: CO_SALES_REP" config on the CUSTOMER ID(s) visual).
const REP_PALETTE = [
  "#8E44AD", "#C0392B", "#D4A017", "#16A085", "#2980B9",
  "#E91E8C", "#27AE60", "#E67E22", "#7F8C8D", "#34495E",
  "#1ABC9C", "#9B59B6", "#D35400", "#2C3E50", "#C2185B",
];

// Country name / code -> ISO 3166-1 alpha-2, for the flag in the first column. Covers the values in
// rpt_CustomerOrders.CustomerCountry (raw codes USA/CAN/MEX + full names) plus common export markets.
// Emoji flags render as bare letter-pairs on Windows, so we use small flag images keyed by this code.
const COUNTRY_ISO = {
  "UNITED STATES": "us", "UNITED STATES OF AMERICA": "us", "USA": "us", "US": "us", "U.S.A.": "us", "U.S.": "us",
  "CANADA": "ca", "CAN": "ca", "MEXICO": "mx", "MEX": "mx",
  "BRAZIL": "br", "BRASIL": "br", "ARGENTINA": "ar", "CHILE": "cl", "PERU": "pe", "COLOMBIA": "co",
  "BOLIVIA": "bo", "ECUADOR": "ec", "VENEZUELA": "ve", "URUGUAY": "uy", "PARAGUAY": "py",
  "AUSTRALIA": "au", "NEW ZEALAND": "nz",
  "UNITED KINGDOM": "gb", "UK": "gb", "ENGLAND": "gb", "IRELAND": "ie", "FRANCE": "fr", "GERMANY": "de",
  "SPAIN": "es", "PORTUGAL": "pt", "ITALY": "it", "GREECE": "gr", "CYPRUS": "cy", "NETHERLANDS": "nl",
  "BELGIUM": "be", "SWITZERLAND": "ch", "AUSTRIA": "at", "SWEDEN": "se", "NORWAY": "no", "DENMARK": "dk",
  "FINLAND": "fi", "POLAND": "pl", "TURKEY": "tr", "RUSSIA": "ru",
  "INDIA": "in", "CHINA": "cn", "JAPAN": "jp", "SOUTH KOREA": "kr", "KOREA": "kr", "INDONESIA": "id",
  "THAILAND": "th", "VIETNAM": "vn", "PHILIPPINES": "ph", "MALAYSIA": "my", "SINGAPORE": "sg",
  "SOUTH AFRICA": "za", "EGYPT": "eg", "NIGERIA": "ng", "KENYA": "ke", "GHANA": "gh", "MOROCCO": "ma",
  "SAUDI ARABIA": "sa", "UNITED ARAB EMIRATES": "ae", "UAE": "ae", "ISRAEL": "il", "QATAR": "qa", "KUWAIT": "kw",
  "GUATEMALA": "gt", "HONDURAS": "hn", "COSTA RICA": "cr", "PANAMA": "pa", "NICARAGUA": "ni",
  "EL SALVADOR": "sv", "DOMINICAN REPUBLIC": "do", "JAMAICA": "jm", "TRINIDAD AND TOBAGO": "tt", "CUBA": "cu",
};

export default {
  name: "CustomersById",
  props: {
    customers: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },   // currently-selected customer ids (multi)
    // rep -> color override: the report passes each rep's TERRITORY color so these bars match the
    // Sales Reps / Sales by Territory cards (a rep is the same color everywhere). Falls back to the
    // qualitative palette below when not supplied.
    repColorMap: { type: Object, default: null },
    // customerId -> color (the customer's OWN territory color). Preferred over the rep color so bars
    // match the map/chips even when the rep list is narrowed by a customer filter. Falls back to rep.
    customerColorMap: { type: Object, default: null },
    // customerId -> [{territory, salesRep, orderCount}] (desc). When a customer's orders span >1 territory in the
    // current scope, its bar is SEGMENTED by territory (each colored via territoryColorMap). One entry → solid.
    segmentsMap: { type: Object, default: null },
    // territory -> color, so each segment matches the Territory map / Sales-by-Territory colors.
    territoryColorMap: { type: Object, default: null },
    loading: { type: Boolean, default: false },   // a filter reload is in progress → sweep a "processing" line across the rows
  },
  data() { return { search: "" }; },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      const sel = this.selected || [];
      // Selected customers move to chips above and drop out of the list.
      const list = (this.customers || []).filter((c) => sel.indexOf(c.customerId) < 0);
      if (!q) return list;
      return list.filter(
        (c) =>
          (c.customerId || "").toLowerCase().indexOf(q) >= 0 ||
          (c.customerName || "").toLowerCase().indexOf(q) >= 0
      );
    },
    // Scale bars to the busiest customer in the FULL set (stable while searching).
    maxOrders() { return (this.customers || []).reduce((m, c) => Math.max(m, Number(c.orderCount) || 0), 0); },
    // Stable rep -> color map: distinct reps sorted, assigned by index.
    repColors() {
      const reps = Array.from(new Set((this.customers || []).map((c) => c.salesRep || ""))).sort();
      const map = {};
      reps.forEach((r, i) => { map[r] = REP_PALETTE[i % REP_PALETTE.length]; });
      return map;
    },
  },
  methods: {
    barPct(v) {
      const m = this.maxOrders;
      if (m <= 0) return "0%";
      // 6%..84% so the trailing count label always has room (matches Power BI data-label placement).
      return (6 + (Number(v) || 0) / m * 78).toFixed(1) + "%";
    },
    repColor(rep) {
      if (this.repColorMap && this.repColorMap[rep || ""]) return this.repColorMap[rep || ""];
      return this.repColors[rep || ""] || "#9e2436";
    },
    // Bar color: the customer's OWN territory color when known (matches the map/chips), else the rep color.
    barColor(c) {
      if (c && this.customerColorMap && this.customerColorMap[c.customerId]) return this.customerColorMap[c.customerId];
      return this.repColor(c && c.salesRep);
    },
    // Territory segments for a customer's bar (empty → render one solid barColor segment).
    segsFor(c) {
      if (this.segmentsMap && c && this.segmentsMap[c.customerId]) return this.segmentsMap[c.customerId];
      return [];
    },
    // Color for a territory segment (matches the map / Sales-by-Territory), with a safe fallback.
    segColor(territory) {
      if (this.territoryColorMap && this.territoryColorMap[territory]) return this.territoryColorMap[territory];
      return "#9e2436";
    },
    fmtNum(n) { return (Number(n) || 0).toLocaleString("en-US"); },
    // Country name/code -> ISO alpha-2 for the flag image (empty -> no flag, e.g. blank/unknown country).
    flagIso(country) {
      if (!country) return "";
      return COUNTRY_ISO[String(country).trim().toUpperCase()] || "";
    },
  },
};
</script>

<style scoped>
.cb { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
.cb-sub { font-size: 11px; color: #777; padding: 0 14px 6px; }
.cb-searchwrap { position: relative; padding: 0 14px 8px; }
.cb-search { width: 100%; font-size: 12px; padding: 5px 24px 5px 8px; border: 1px solid #d6d6d6; border-radius: 6px; }
.cb-search:focus { outline: none; border-color: #9e2436; }
.cb-clear { position: absolute; right: 20px; top: 3px; border: 0; background: none; color: #999; font-size: 16px; cursor: pointer; line-height: 1; }
/* The customer rows FILL the card's available height and scroll internally (search box stays fixed above) — no
   fixed ~4-row cap. Desktop: the parent card body is a flex column (see the scoped rule in CustomerInsights.vue)
   so this list flex-grows to the card height; mobile: the card auto-heights, so the list shows every row. */
.cb-list { overflow-y: auto; flex: 1 1 auto; min-height: 0; padding: 0 14px 4px; }
.cb-row { display: grid; grid-template-columns: 22px 54px 1fr 16px 16px 16px; align-items: center; gap: 5px; margin-bottom: 4px; cursor: pointer; border-radius: 4px; padding: 1px 3px; }
/* Icon cluster (last 3 columns, each always reserved so bars stay aligned whether or not a customer has a note/spec):
   💡 AI perspective · 📋 specifications · 📝 notation. */
.cb-ic { display: flex; align-items: center; justify-content: center; min-width: 0; overflow: hidden; }
.cb-note, .cb-spec, .cb-persp { border: 0; background: none; cursor: pointer; font-size: 12px; line-height: 1; padding: 0; opacity: .82; }
.cb-note:hover, .cb-spec:hover, .cb-persp:hover { opacity: 1; transform: scale(1.12); }
.cb-row:hover { background: #f3f4f6; }
.cb-row--sel { background: #fdecee; box-shadow: inset 0 0 0 1px #9e2436; }
.cb-flag { width: 22px; height: 16px; object-fit: cover; border-radius: 2px; box-shadow: 0 0 0 1px rgba(0,0,0,.14); display: block; }
.cb-flag--none { box-shadow: none; background: transparent; }
.cb-id { font-size: 11px; color: #444; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cb-bartrack { display: flex; align-items: center; height: 16px; }
.cb-bar { height: 100%; border-radius: 3px; min-width: 4px; transition: width .3s ease; display: flex; overflow: hidden; }
.cb-seg { height: 100%; min-width: 2px; }
.cb-seg + .cb-seg { box-shadow: inset 1px 0 0 rgba(255,255,255,.55); }   /* thin divider between territory segments */
.cb-count { font-size: 10px; font-weight: 600; color: #555; margin-left: 5px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.cb-empty { padding: 16px; text-align: center; color: #999; font-size: 12px; }
/* "Processing" sweep while a filter reloads (rows stay visible, dimmed). */
.cb-loading .cb-row { position: relative; opacity: .85; }
.cb-loading .cb-row::after { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px; pointer-events: none; background: linear-gradient(90deg, rgba(158,36,54,0) 0%, rgba(158,36,54,0) 42%, #9e2436 50%, rgba(158,36,54,0) 58%, rgba(158,36,54,0) 100%); background-size: 230% 100%; animation: cb-sweep 1.15s linear infinite; }
@keyframes cb-sweep { from { background-position: 130% 0; } to { background-position: -130% 0; } }
</style>
